import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon, 
  UserCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldExclamationIcon,
  XMarkIcon,
  GiftIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detectedRef, setDetectedRef] = useState('');
  const [availableUsernames, setAvailableUsernames] = useState([]);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const referralCode = queryParams.get('ref');
    
    if (referralCode) {
      setFormData(prev => ({
        ...prev,
        referralCode: referralCode.trim().toUpperCase()
      }));
      setDetectedRef(referralCode.trim().toUpperCase());
      setSuccess(`🎁 Referral code "${referralCode}" detected! You'll receive bonus rewards.`);
      
      setTimeout(() => setSuccess(''), 5000);
    }
  }, [location.search]);

  useEffect(() => {
    let interval;
    if (rateLimitError && retryAfter > 0) {
      interval = setInterval(() => {
        setRetryAfter(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setRateLimitError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => interval && clearInterval(interval);
  }, [rateLimitError, retryAfter]);

  const generateAlternatives = (username) => {
    const alternatives = [];
    for (let i = 1; i <= 5; i++) {
      alternatives.push(`${username}${i}`);
      alternatives.push(`${username}_${i}`);
      alternatives.push(`${username}${String.fromCharCode(96 + i)}`);
    }
    return alternatives.slice(0, 5);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    if (name !== 'password' && name !== 'confirmPassword') {
      processedValue = value.trim();
    }
    if (name === 'referralCode') {
      processedValue = value.toUpperCase();
    }
    if (name === 'referralCode' && !processedValue && detectedRef) {
      setDetectedRef('');
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    setError('');
    setSuccess('');
    setRateLimitError(null);
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('8+ characters');
    if (!/[A-Z]/.test(password)) errors.push('Uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('Lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('Number');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('Special character');
    return errors;
  };

  const extractRetryAfter = (error) => {
    if (error.response?.headers?.['retry-after']) {
      return parseInt(error.response.headers['retry-after'], 10);
    }
    if (error.response?.data?.retryAfter) {
      return parseInt(error.response.data.retryAfter, 10);
    }
    return 60;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAvailableUsernames([]);
    setRateLimitError(null);
    
    if (!formData.username || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (!usernameRegex.test(formData.username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(`Password missing: ${passwordErrors.join(', ')}`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.referralCode) {
      const refRegex = /^[A-Z0-9]{6,12}$/;
      if (!refRegex.test(formData.referralCode)) {
        setError('Referral code must be 6-12 uppercase alphanumeric characters');
        return;
      }
    }

    try {
      setLoading(true);
      
      const userData = {
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        referralCode: formData.referralCode.trim() || undefined,
      };

      const response = await authAPI.register(userData);

      if (response.data?.success) {
        let successMsg = '🎉 Registration successful! ';
        if (detectedRef) {
          successMsg += `You joined with referral code: ${detectedRef}`;
        } else if (formData.referralCode) {
          successMsg += `You used referral code: ${formData.referralCode}`;
        } else {
          successMsg += 'Welcome to our platform!';
        }
        
        setSuccess(successMsg);
        
        localStorage.setItem('registration_success', JSON.stringify({
          username: formData.username,
          email: formData.email,
          referralCode: formData.referralCode,
          timestamp: new Date().toISOString(),
          message: successMsg
        }));
        
        const tempUserData = {
          username: formData.username,
          email: formData.email,
          registeredAt: new Date().toISOString()
        };
        localStorage.setItem('temp_user_data', JSON.stringify(tempUserData));
        
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              registered: true,
              username: formData.username,
              email: formData.email,
              message: successMsg
            } 
          });
        }, 3000);
        
      } else {
        throw new Error(response.data?.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.response?.status === 429) {
        const retryAfterTime = extractRetryAfter(err);
        setRetryAfter(retryAfterTime);
        setRateLimitError({
          message: err.response?.data?.message || 'Too many registration attempts',
          retryAfter: retryAfterTime,
          timestamp: new Date().toISOString()
        });
        
        localStorage.setItem('rate_limit_registration', JSON.stringify({
          timestamp: new Date().toISOString(),
          retryAfter: retryAfterTime,
          email: formData.email,
          username: formData.username
        }));
        return;
      }
      
      if (err.response?.status === 409) {
        const errorData = err.response?.data;
        const details = errorData?.details;
        
        if (details?.conflict === 'username') {
          const alternatives = generateAlternatives(formData.username);
          setAvailableUsernames(alternatives);
          setError(`Username "${formData.username}" is already taken. Try one of these:`);
        } else if (details?.conflict === 'email') {
          setError(`Email "${formData.email}" is already registered. Please use another email or try logging in.`);
        } else {
          setError(errorData?.message || 'Username or email already exists.');
        }
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Invalid registration data.');
      } else if (err.response?.status === 404) {
        setError('Referral code not found. Please check and try again.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again in a few moments.');
      } else if (err.message === 'Network Error') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(err.response?.data?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAltUsernameClick = (altUsername) => {
    setFormData(prev => ({
      ...prev,
      username: altUsername
    }));
    setError('');
    setAvailableUsernames([]);
  };

  const RateLimitDisplay = () => {
    if (!rateLimitError) return null;

    return (
      <div style={{
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.15)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ShieldExclamationIcon style={{ width: '1.25rem', height: '1.25rem', color: '#f87171' }} />
            <h3 style={{ fontWeight: '600', color: '#fecaca', fontSize: '0.875rem' }}>Too Many Requests</h3>
          </div>
          <button onClick={() => setRateLimitError(null)} style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer' }}>
            <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '0.75rem' }}>
          <ExclamationTriangleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#fbbf24' }} />
          <div>
            <p style={{ fontSize: '0.875rem', color: '#fecaca', marginBottom: '0.25rem' }}>{rateLimitError.message}</p>
            <p style={{ fontSize: '0.75rem', color: '#fecaca' }}>Please wait before trying again to prevent abuse.</p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '0.75rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
          <ClockIcon style={{ width: '1.25rem', height: '1.25rem', color: '#f87171' }} />
          <div>
            <p style={{ fontSize: '0.75rem', color: '#fecaca' }}>Retry in:</p>
            <p style={{ fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 'bold', color: 'white' }}>
              {Math.floor(retryAfter / 60)}:{String(retryAfter % 60).padStart(2, '0')}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button 
            onClick={() => navigate('/login')}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '0.75rem',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem'
            }}
          >
            Already have an account? Login
          </button>
          <button 
            onClick={() => window.location.reload()}
            disabled={retryAfter > 0}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '0.75rem',
              color: 'white',
              cursor: retryAfter > 0 ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              opacity: retryAfter > 0 ? 0.5 : 1,
              fontSize: '0.875rem'
            }}
          >
            {retryAfter > 0 ? 'Please wait...' : 'Refresh Page'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      background: '#0a0a0a',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background - Cyan Accents */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15), transparent)',
        borderRadius: '50%',
        top: '-250px',
        right: '-250px',
        animation: 'pulse 3s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(0, 212, 255, 0.08), transparent)',
        borderRadius: '50%',
        bottom: '-300px',
        left: '-300px',
        animation: 'pulse 4s ease-in-out infinite reverse'
      }} />

      {/* Register Card - Dark Theme */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        width: '100%',
        maxWidth: '520px',
        background: '#1a1a2e',
        backdropFilter: 'blur(20px)',
        borderRadius: '2rem',
        border: '1px solid rgba(0, 212, 255, 0.4)',
        boxShadow: '0 25px 50px -12px rgba(0, 212, 255, 0.2)',
        padding: '2rem',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        
        {/* Header - Updated to Cyan */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            width: '70px',
            height: '70px',
            background: '#00d4ff',
            borderRadius: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem',
            boxShadow: '0 10px 25px -5px rgba(0, 212, 255, 0.4)'
          }}>
            <UserCircleIcon style={{ width: '2rem', height: '2rem', color: '#1a1a2e' }} />
          </div>
          <h1 style={{ 
            fontSize: '1.875rem', 
            fontWeight: 'bold', 
            background: 'linear-gradient(135deg, #fff, #00d4ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem' 
          }}>Create Account</h1>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Join our investment platform</p>
        </div>

        {/* Referral Alert - Dark Theme */}
        {detectedRef && (
          <div style={{
            marginBottom: '1.25rem',
            padding: '0.75rem 1rem',
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <GiftIcon style={{ width: '1.25rem', height: '1.25rem', color: '#00d4ff' }} />
              <div>
                <p style={{ fontSize: '0.875rem', color: '#00d4ff', fontWeight: '500' }}>Referral Bonus Active!</p>
                <p style={{ fontSize: '0.75rem', color: '#00d4ff' }}>Using code: <strong style={{ fontFamily: 'monospace' }}>{detectedRef}</strong></p>
              </div>
            </div>
          </div>
        )}

        {/* Rate Limit Error */}
        <RateLimitDisplay />

        {/* Error Message - Dark Theme */}
        {error && !rateLimitError && (
          <div style={{
            marginBottom: '1.25rem',
            padding: '0.875rem 1rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                <ExclamationTriangleIcon style={{ width: '1rem', height: '1rem', color: '#f87171', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#fecaca' }}>{error}</span>
                  {availableUsernames.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <p style={{ fontSize: '0.75rem', color: '#fecaca', marginBottom: '0.5rem' }}>Available alternatives:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {availableUsernames.map(alt => (
                          <button
                            key={alt}
                            type="button"
                            onClick={() => handleAltUsernameClick(alt)}
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '0.5rem',
                              color: '#fecaca',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {alt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => {
                  setError('');
                  setAvailableUsernames([]);
                }}
                style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
              >
                <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
          </div>
        )}

        {/* Success Message - Dark Theme */}
        {success && (
          <div style={{
            marginBottom: '1.25rem',
            padding: '0.875rem 1rem',
            background: 'rgba(0, 212, 255, 0.15)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <CheckCircleIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              <span style={{ fontSize: '0.875rem', color: '#00d4ff' }}>{success}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#00d4ff' }}>
              <div style={{ width: '0.75rem', height: '0.75rem', border: '2px solid #00d4ff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span>Redirecting to login in 3 seconds...</span>
            </div>
          </div>
        )}

        {/* Registration Form - Dark Theme */}
        <form onSubmit={handleSubmit}>
          {/* Username Input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              <UserIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              Username *
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '1rem',
                color: 'white',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
              required
              disabled={loading || rateLimitError}
            />
            <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>3-20 characters, letters, numbers, underscores</p>
          </div>

          {/* Email Input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              <EnvelopeIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '1rem',
                color: 'white',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
              required
              disabled={loading || rateLimitError}
            />
          </div>

          {/* Password and Confirm Password Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            {/* Password Input */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                <LockClosedIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
                Password *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create password"
                  style={{
                    width: '100%',
                    padding: '0.875rem 2.5rem 0.875rem 1rem',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '1rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
                  required
                  disabled={loading || rateLimitError}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8'
                  }}
                >
                  {showPassword ? (
                    <EyeSlashIcon style={{ width: '1rem', height: '1rem' }} />
                  ) : (
                    <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                <LockClosedIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
                Confirm Password *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  style={{
                    width: '100%',
                    padding: '0.875rem 2.5rem 0.875rem 1rem',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '1rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
                  required
                  disabled={loading || rateLimitError}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8'
                  }}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon style={{ width: '1rem', height: '1rem' }} />
                  ) : (
                    <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {/* Password Requirements - Dark Theme */}
          <div style={{
            padding: '0.875rem',
            background: '#0f0f0f',
            borderRadius: '1rem',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            marginBottom: '1rem'
          }}>
            <h4 style={{ fontSize: '0.7rem', fontWeight: '600', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Password Requirements:
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.25rem' }}>
              {validatePassword(formData.password).map((req, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <span>○</span>
                  <span>{req}</span>
                </div>
              ))}
              {validatePassword(formData.password).length === 0 && formData.password && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.7rem', color: '#00d4ff' }}>
                  <CheckCircleIcon style={{ width: '0.7rem', height: '0.7rem' }} />
                  <span>All requirements met!</span>
                </div>
              )}
            </div>
          </div>

          {/* Referral Code - Dark Theme */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              <GiftIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              Referral Code {detectedRef ? '(Auto-detected)' : '(Optional)'}
            </label>
            <input
              type="text"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              placeholder="Enter referral code"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                background: detectedRef ? 'rgba(0, 212, 255, 0.15)' : '#0f0f0f',
                border: detectedRef ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '1rem',
                color: 'white',
                fontSize: '0.875rem',
                outline: 'none',
                cursor: detectedRef ? 'not-allowed' : 'text'
              }}
              disabled={loading || !!detectedRef || rateLimitError}
            />
            {detectedRef && (
              <p style={{ fontSize: '0.7rem', color: '#00d4ff', marginTop: '0.25rem' }}>
                Code detected from URL. You can't change it here.
              </p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div style={{ margin: '1rem 0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                style={{ width: '1rem', height: '1rem', accentColor: '#00d4ff' }}
                required 
                disabled={loading || rateLimitError}
              />
              <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                I confirm that I am at least 18 years old
              </span>
            </label>
          </div>

          {/* Submit Button - Updated to Cyan */}
          <button
            type="submit"
            disabled={loading || rateLimitError}
            style={{
              width: '100%',
              padding: '0.875rem',
              borderRadius: '1rem',
              fontWeight: '600',
              color: loading || rateLimitError ? '#94a3b8' : '#1a1a2e',
              border: 'none',
              cursor: loading || rateLimitError ? 'not-allowed' : 'pointer',
              background: loading || rateLimitError ? '#334155' : '#00d4ff',
              transition: 'all 0.2s',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => {
              if (!loading && !rateLimitError) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 212, 255, 0.4)';
                e.target.style.background = '#00e5ff';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
              if (!loading && !rateLimitError) {
                e.target.style.background = '#00d4ff';
              }
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <div style={{ width: '1.25rem', height: '1.25rem', border: '2px solid #1a1a2e', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span>Creating Account...</span>
              </div>
            ) : (
              rateLimitError ? 'Temporarily Disabled' : 'Create Account'
            )}
          </button>
        </form>

        {/* Footer - Dark Theme */}
        <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(0, 212, 255, 0.2)', textAlign: 'center' }}>
          <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#00d4ff', fontWeight: '600', textDecoration: 'none' }}>
              Sign in here
            </Link>
          </p>
          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.5rem' }}>
            By signing up, you agree to receive occasional updates about your account.
          </p>
          {rateLimitError && (
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.7rem', color: '#fbbf24' }}>
              <ClockIcon style={{ width: '0.75rem', height: '0.75rem' }} />
              <span>Registration temporarily limited. Try again in {retryAfter} seconds.</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Register;